/**
 * Returns true only when every RGBA channel of the pixel at byte offset i is
 * independently within thresh of the seed color.  Using per-channel AND logic
 * (Chebyshev / L∞ distance) prevents a large deviation in one channel from
 * being masked by closeness in another — pure red and pure blue always fail
 * the blue/red channel check regardless of how well the other channels match.
 */
function colorMatches(
  buf: Uint8Array, i: number,
  r: number, g: number, b: number, a: number,
  thresh: number,
): boolean {
  return (
    Math.abs(buf[i]     - r) <= thresh &&
    Math.abs(buf[i + 1] - g) <= thresh &&
    Math.abs(buf[i + 2] - b) <= thresh &&
    Math.abs(buf[i + 3] - a) <= thresh
  );
}

/**
 * Flip a pixel buffer between WebGL bottom-up and canvas top-down orientation
 * in place. The operation is its own inverse.
 */
export function flipBufferY(buf: Uint8Array, w: number, h: number): void {
  const rowBytes = w * 4;
  const tmp = new Uint8Array(rowBytes);
  for (let y = 0; y < (h >> 1); y++) {
    const top = y * rowBytes;
    const bot = (h - 1 - y) * rowBytes;
    tmp.set(buf.subarray(top, top + rowBytes));
    buf.set(buf.subarray(bot, bot + rowBytes), top);
    buf.set(tmp, bot);
  }
}

/** A single color stop for a multi-stop gradient fill.
 *  `pos` is a normalized position in [0,1] along the gradient axis. */
export interface GradientStop {
  r: number;
  g: number;
  b: number;
  pos: number;
}

/** Fill mode passed to buildFillMask, floodFill and floodFillGradient.
 *  - 'island'       BFS flood from seed, stops at color boundaries.
 *  - 'noncontiguous' Replace all pixels that match the seed color anywhere in the layer.
 *  - 'global'       Fill the entire layer regardless of existing content (ignores tolerance).
 */
export type FillMode = 'island' | 'noncontiguous' | 'global';

/**
 * Builds a fill mask without painting.  Returns a Uint8Array of length
 * width*height where 1 = pixel should be filled, 0 = leave alone.
 */
export function buildFillMask(
  pixels: Uint8Array,
  width: number,
  height: number,
  seedX: number,
  seedY: number,
  tolerance: number,
  mode: FillMode,
): Uint8Array {
  const mask = new Uint8Array(width * height);

  if (mode === 'global') {
    mask.fill(1);
    return mask;
  }

  seedX = Math.max(0, Math.min(width  - 1, seedX));
  seedY = Math.max(0, Math.min(height - 1, seedY));

  const si = (seedY * width + seedX) * 4;
  const seedR = pixels[si];
  const seedG = pixels[si + 1];
  const seedB = pixels[si + 2];
  const seedA = pixels[si + 3];

  const thresh = Math.round(tolerance * 1.27);
  const matches = (i: number): boolean =>
    colorMatches(pixels, i, seedR, seedG, seedB, seedA, thresh);

  if (mode === 'noncontiguous') {
    const len = width * height;
    for (let p = 0; p < len; p++) {
      if (matches(p * 4)) mask[p] = 1;
    }
    return mask;
  }

  // mode === 'island': BFS from seed
  const queue = new Int32Array(width * height);
  let head = 0, tail = 0;
  const seed1d = seedY * width + seedX;
  queue[tail++] = seed1d;
  mask[seed1d] = 1;

  while (head < tail) {
    const pos = queue[head++];
    const x = pos % width;
    const y = (pos / width) | 0;

    const left  = x > 0          ? pos - 1       : pos + width - 1;
    const right = x < width - 1  ? pos + 1       : pos - width + 1;
    const up    = y > 0          ? pos - width    : -1;
    const down  = y < height - 1 ? pos + width    : -1;

    for (const n of [left, right, up, down]) {
      if (n >= 0 && !mask[n] && matches(n * 4)) {
        mask[n] = 1;
        queue[tail++] = n;
      }
    }
  }

  return mask;
}

/**
 * In-place flood fill on a flat RGBA Uint8Array (top-down, left-to-right).
 *
 * @param pixels     - RGBA pixel data, row-major, 4 bytes per pixel.
 * @param width      - Texture width in pixels.
 * @param height     - Texture height in pixels.
 * @param seedX      - Seed pixel column, 0-indexed.
 * @param seedY      - Seed pixel row, 0 = top.
 * @param fillR      - Fill color red component (0-255).
 * @param fillG      - Fill color green component (0-255).
 * @param fillB      - Fill color blue component (0-255).
 * @param fillA      - Fill color alpha component (0-255).
 * @param tolerance  - Match tolerance 0-100, mapped to per-channel threshold 0-127.
 * @param mode       - 'island' = BFS flood; 'noncontiguous' = all matching pixels; 'global' = all pixels.
 */
export function floodFill(
  pixels: Uint8Array,
  width: number,
  height: number,
  seedX: number,
  seedY: number,
  fillR: number,
  fillG: number,
  fillB: number,
  fillA: number,
  tolerance: number,
  mode: FillMode,
): void {
  const mask = buildFillMask(pixels, width, height, seedX, seedY, tolerance, mode);
  const len = width * height;
  for (let p = 0; p < len; p++) {
    if (mask[p]) {
      const i = p * 4;
      pixels[i] = fillR; pixels[i + 1] = fillG; pixels[i + 2] = fillB; pixels[i + 3] = fillA;
    }
  }
}

/**
 * Flood fill with a two-color gradient projected along the drag axis,
 * spherically aware so that gradients wrap correctly over the sphere
 * rather than pinching at the poles in UV space.
 *
 * The fill region is determined by `mode` exactly like floodFill.
 * Each pixel in the region is painted with a color interpolated across the
 * provided gradient stops based on the 3D Euclidean projection of the pixel's
 * sphere direction onto the chord from the start-sphere-point to the
 * end-sphere-point.
 *
 * When start === end (bare click, no drag) the fill degenerates to a flat
 * fill with the first stop's color.
 *
 * The texture is treated as an equirectangular projection:
 *   theta = U * 2π  (longitude)
 *   phi   = V * π   (colatitude, 0 = north pole)
 *   3D = (sin(phi)*cos(theta), cos(phi), sin(phi)*sin(theta))
 *
 * @param pixels      - RGBA pixel data, row-major top-down, 4 bytes per pixel.
 * @param width       - Texture width in pixels.
 * @param height      - Texture height in pixels.
 * @param seedX       - Seed pixel column (click start), 0-indexed.
 * @param seedY       - Seed pixel row (click start), 0 = top.
 * @param startU      - Click-start U coordinate [0,1].
 * @param startV      - Click-start V coordinate [0,1], 0 = top.
 * @param endU        - Click-release U coordinate [0,1].
 * @param endV        - Click-release V coordinate [0,1], 0 = top.
 * @param stops       - Gradient color stops sorted by pos ascending.
 * @param tolerance   - Match tolerance 0-100.
 * @param mode        - Fill mode (island / noncontiguous / global).
 */
export function floodFillGradient(
  pixels: Uint8Array,
  width: number,
  height: number,
  seedX: number,
  seedY: number,
  startU: number,
  startV: number,
  endU: number,
  endV: number,
  stops: GradientStop[],
  tolerance: number,
  mode: FillMode,
): void {
  if (stops.length === 0) return;
  const mask = buildFillMask(pixels, width, height, seedX, seedY, tolerance, mode);

  // Extract stop data into flat arrays for cache-friendly, allocation-free access.
  const nStops = stops.length;
  const sR = new Uint8Array(nStops);
  const sG = new Uint8Array(nStops);
  const sB = new Uint8Array(nStops);
  const sP = new Float32Array(nStops);
  for (let si = 0; si < nStops; si++) {
    sR[si] = stops[si].r; sG[si] = stops[si].g; sB[si] = stops[si].b; sP[si] = stops[si].pos;
  }

  // Convert start/end UV to 3D unit-sphere directions (equirectangular).
  const sTheta = startU * Math.PI * 2;
  const sPhi   = startV * Math.PI;
  const sx = Math.sin(sPhi) * Math.cos(sTheta);
  const sy = Math.cos(sPhi);
  const sz = Math.sin(sPhi) * Math.sin(sTheta);

  const eTheta = endU * Math.PI * 2;
  const ePhi   = endV * Math.PI;
  const ex = Math.sin(ePhi) * Math.cos(eTheta);
  const ey = Math.cos(ePhi);
  const ez = Math.sin(ePhi) * Math.sin(eTheta);

  // Chord vector (axis of gradient in 3D Euclidean space).
  const ax = ex - sx, ay = ey - sy, az = ez - sz;
  const aLenSq = ax * ax + ay * ay + az * az;

  if (aLenSq < 1e-10) {
    // Degenerate: no drag — flat fill with first stop's color.
    const len = width * height;
    for (let p = 0; p < len; p++) {
      if (mask[p]) {
        const i = p * 4;
        pixels[i] = sR[0]; pixels[i + 1] = sG[0]; pixels[i + 2] = sB[0]; pixels[i + 3] = 255;
      }
    }
    return;
  }

  const invALenSq = 1 / aLenSq;

  // Precompute trig look-up tables to avoid per-pixel trig calls.
  const cosTheta = new Float32Array(width);
  const sinTheta = new Float32Array(width);
  for (let col = 0; col < width; col++) {
    const theta = (col / width) * Math.PI * 2;
    cosTheta[col] = Math.cos(theta);
    sinTheta[col] = Math.sin(theta);
  }
  const sinPhi = new Float32Array(height);
  const cosPhi = new Float32Array(height);
  for (let row = 0; row < height; row++) {
    const phi = (row / height) * Math.PI;
    sinPhi[row] = Math.sin(phi);
    cosPhi[row] = Math.cos(phi);
  }

  const len = width * height;
  for (let p = 0; p < len; p++) {
    if (!mask[p]) continue;
    const col = p % width;
    const row = (p / width) | 0;

    // Pixel direction on the unit sphere.
    const sp = sinPhi[row];
    const cp = cosPhi[row];
    const px3 = sp * cosTheta[col];
    const py3 = cp;
    const pz3 = sp * sinTheta[col];

    // Scalar projection onto the chord, clamped to [0,1].
    const t = Math.max(0, Math.min(1,
      ((px3 - sx) * ax + (py3 - sy) * ay + (pz3 - sz) * az) * invALenSq,
    ));

    // Multi-stop gradient sample: find the enclosing segment and interpolate.
    let cr = sR[0], cg = sG[0], cb = sB[0];
    if (t >= sP[nStops - 1]) {
      cr = sR[nStops - 1]; cg = sG[nStops - 1]; cb = sB[nStops - 1];
    } else if (t > sP[0]) {
      for (let si = 0; si < nStops - 1; si++) {
        if (t <= sP[si + 1]) {
          const span = sP[si + 1] - sP[si];
          const lt = span < 1e-10 ? 0 : (t - sP[si]) / span;
          cr = Math.round(sR[si] + (sR[si + 1] - sR[si]) * lt);
          cg = Math.round(sG[si] + (sG[si + 1] - sG[si]) * lt);
          cb = Math.round(sB[si] + (sB[si + 1] - sB[si]) * lt);
          break;
        }
      }
    }

    const i = p * 4;
    pixels[i] = cr; pixels[i + 1] = cg; pixels[i + 2] = cb; pixels[i + 3] = 255;
  }
}

